/* eslint-disable @typescript-eslint/no-explicit-any */
import { logger } from '@marriott/shared/mi-helper-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import {
  cobrandDatalayer,
  propertiesById,
  phoenixFetchOrderDetails,
  phoenixBookCartSOC,
  phoenixReservationConfirmation,
} from '../../queries';
import operationSignatures from './_constants/operationSignatures.json';
import { ApolloClient, NormalizedCacheObject, DocumentNode, QueryOptions } from '@apollo/client';
import { SessionData } from '../session';
import { IConsumerDataResponse } from './types/consumerUXlResponse.types';
import { ITripsInfo, IPropertiesByIdResponse } from './types/trips.types';
import { AUTH_STATE, PAGE_URI, RENDERER, RENDERER_TYPE } from '../constants';
import { getMemState } from '../dataLayer/datalayerHelpers';

type CreateQueryConfigParams<TVariables> = {
  query: DocumentNode;
  variables: TVariables;
  sessionData: SessionData;
};

const getHeaders = (sessionData: SessionData) => {
  const memState = getMemState(sessionData);
  if (memState === AUTH_STATE.remembered) {
    return {
      'access-type': 'rememberMeState',
    };
  } else return {};
};

// Refined createQueryConfig function with types for query variables
const createQueryConfig = <TVariables>({
  query,
  variables,
  sessionData,
}: CreateQueryConfigParams<TVariables>): QueryOptions<any, TVariables> => ({
  query,
  fetchPolicy: 'network-only',
  errorPolicy: 'all',
  variables,
  context: {
    headers: { ...getHeaders(sessionData) },
  },
});

const getQueryPromises = (
  MiApolloClient: ApolloClient<NormalizedCacheObject>,
  sessionData: SessionData,
  pageURI = '',
  renderer: string
) => {
  const customerId = sessionData?.data?.consumerID ?? '';
  const rewardsId = sessionData?.data?.rewardsId ?? '';
  const authState = getMemState(sessionData);
  const ariesReservationSession = sessionData?.data?.AriesReservation;
  const orderId = ariesReservationSession?.tripId;
  const cartId = ariesReservationSession?.shoppingCartId;
  const propertyId = pageURI?.startsWith('/einterface/v5')
    ? pageURI?.split('v5/')?.[1]?.substring(0, 5) ?? ariesReservationSession?.propertyId
    : ariesReservationSession?.propertyId || null;

  const marshaCodeRegex = /[a-zA-Z]{5}/;
  return [
    // cobeand specific UXL call
    ...(renderer === RENDERER.COBRAND
      ? [
          {
            key: 'contactInformation',
            promise: MiApolloClient.query(
              createQueryConfig({
                query: cobrandDatalayer,
                variables: {
                  customerId,
                  startYear: 1900,
                  input: { rewardsNumber: rewardsId, status: 'ACTIVE' },
                  propertyId: propertyId ?? '',
                  includeProperty: !!(propertyId && marshaCodeRegex.test(propertyId)),
                  includeInput: !!rewardsId && authState === AUTH_STATE.loggedIn,
                  includeCustomer: !!customerId,
                  isLoggedIn: authState === AUTH_STATE.loggedIn,
                },
                sessionData,
              })
            ),
          },
        ]
      : []),
    // below calls are not used in cobrand as of now.
    // These are added to add the logic for other datalayer keys.
    //  These calls will be moved to renderer specific queries
    ...(orderId?.length &&
    (pageURI.includes(PAGE_URI.confirmReservationCancellation) || pageURI.includes(PAGE_URI.upcomingReservation))
      ? [
          {
            key: 'orderDetails',
            promise: MiApolloClient.query(
              createQueryConfig({
                query: phoenixFetchOrderDetails,
                variables: { orderId },
                sessionData,
              })
            ),
          },
        ]
      : []),
    ...(cartId?.length && pageURI.includes(PAGE_URI.reviewDetails)
      ? [
          {
            key: 'cartDetails',
            promise: MiApolloClient.query(
              createQueryConfig({
                query: phoenixBookCartSOC,
                variables: { cartId },
                sessionData,
              })
            ),
          },
        ]
      : []),
    ...(orderId?.length && pageURI.includes(PAGE_URI.reservationConfirmation)
      ? [
          {
            key: 'reservationConfirmation',
            promise: MiApolloClient.query(
              createQueryConfig({
                query: phoenixReservationConfirmation,
                variables: { orderId },
                sessionData,
              })
            ),
          },
        ]
      : []),
  ];
};

const getPropertiesByIdQuery = async (
  MiApolloClient: ApolloClient<NormalizedCacheObject>,
  tripsData: ITripsInfo,
  sessionData: SessionData
) => {
  const ids = tripsData?.edges?.map(edge => edge?.node?.items?.[0]?.property?.id);
  const response = await MiApolloClient.query(
    createQueryConfig({
      query: propertiesById,
      variables: { ids },
      sessionData,
    })
  )
    ?.then(data => data)
    ?.catch(err => {
      console.error(err);
    });
  return response?.data ?? null;
};

// Main function to execute all queries
export async function fetchConsumerData(sessionData: SessionData, pageURI = '', renderer: RENDERER_TYPE) {
  const { log } = logger({})('fetchConsumerData');
  try {
    const MiApolloClient = useNextMiApolloClient(
      operationSignatures,
      useClientEnvVarsStore.getState().envVarsObject['DEPLOYED_ENV_TYPE']
    );

    // Create query promises with respective keys
    const queryPromises = getQueryPromises(MiApolloClient, sessionData, pageURI, renderer);

    // Execute all queries and handle results
    const results = (await Promise.allSettled(queryPromises.map(({ promise }) => promise))) as any;

    const response: IConsumerDataResponse = results.reduce(
      (acc: Record<string, string>, result: any, index: number) => {
        const { key } = queryPromises[index];
        acc[key] = result.status === 'fulfilled' && !result.value.errors ? result.value.data : null;
        return acc;
      },
      {}
    );
    if (response?.contactInformation !== null && response?.contactInformation?.searchOrdersByRewardsNumber) {
      const propertiesByIdResponse = (await getPropertiesByIdQuery(
        MiApolloClient,
        response.contactInformation.searchOrdersByRewardsNumber,
        sessionData
      )) as IPropertiesByIdResponse | null;
      response.propertiesByIds = propertiesByIdResponse?.propertiesByIds;
    }

    console.log('conumer UXL response :', response);
    return response;
  } catch (error) {
    log.error('Error in fetching consumer data', error);
    return null;
  }
}
